import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Check } from "lucide-react";
import { OrchestratorTile } from "@/components/brand/OrchestratorLogo";
import HeaderControls from "@/components/HeaderControls";

const TIERS = [
    { id: "solo", name: "Solo", price: "€99", credits: "500 credits" },
    { id: "team", name: "Team", price: "€399", credits: "2.500 credits" },
    { id: "agency", name: "Agency", price: "€1.299", credits: "10.000 credits" },
];

export default function Register() {
    const { t } = useTranslation();
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [form, setForm] = useState({ name: "", email: "", password: "", workspace_name: "", tier: "team" });
    const [loading, setLoading] = useState(false);

    // Prefill from Agent-Diagnose CTA
    useEffect(() => {
        const em = params.get("prefill_email");
        const co = params.get("prefill_company");
        if (em || co) {
            setForm((f) => ({
                ...f,
                email: em ? decodeURIComponent(em) : f.email,
                workspace_name: co ? decodeURIComponent(co) : f.workspace_name,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await register(form);
            toast.success(t("auth.reg_created_toast"));
            nav("/app/home");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("auth.reg_failed"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen px-6 py-12 flex items-center justify-center">
            {/* Top-right language + theme controls, always visible for public pages */}
            <div className="absolute top-4 right-4 z-10">
                <HeaderControls variant="landing" />
            </div>
            <div className="w-full max-w-2xl jr-fadein">
                <Link to="/" className="flex items-center gap-2 mb-8" data-testid="register-logo">
                    <OrchestratorTile size={32} />
                    <span className="font-display text-2xl tracking-tight">Orchestrator</span>
                </Link>
                <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold leading-tight">{t("auth.reg_title")}</h1>
                <p className="mt-2 text-[var(--jr-text-2)]">{t("auth.reg_intro")}</p>

                <div className="mt-4 rounded-lg border border-[var(--jr-info)]/25 bg-[var(--jr-info)]/[0.06] px-3 py-2 text-xs text-[var(--jr-text-2)]" data-testid="multi-workspace-hint">
                    <span className="text-[var(--jr-info)] font-medium">{t("auth.reg_hint_head")}</span>{" "}
                    {t("auth.reg_hint_body")}
                </div>

                <form onSubmit={submit} className="mt-6 space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label={t("auth.reg_name")} v={form.name} onChange={set("name")} testid="reg-name" required/>
                        <Field label={t("auth.reg_workspace")} v={form.workspace_name} onChange={set("workspace_name")} testid="reg-workspace" required/>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label={t("auth.email")} type="email" v={form.email} onChange={set("email")} testid="reg-email" required/>
                        <Field label={t("auth.password")} type="password" v={form.password} onChange={set("password")} testid="reg-password" required min={6}/>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("auth.reg_choose_tier")}</label>
                        <div className="mt-3 grid sm:grid-cols-3 gap-3">
                            {TIERS.map((tier) => {
                                const active = form.tier === tier.id;
                                return (
                                    <button
                                        type="button"
                                        key={tier.id}
                                        data-testid={`reg-tier-${tier.id}`}
                                        onClick={() => setForm({ ...form, tier: tier.id })}
                                        className={`text-left rounded-xl border p-4 transition-all ${
                                            active
                                                ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/5"
                                                : "border-white/10 bg-[var(--jr-surface)] hover:border-white/20"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-display text-lg">{tier.name}</span>
                                            {active && <Check size={16} className="text-[var(--jr-brand)]"/>}
                                        </div>
                                        <div className="font-display text-2xl tracking-tighter mt-2">{tier.price}<span className="text-sm text-[var(--jr-text-2)]"> /mnd</span></div>
                                        <div className="text-xs text-[var(--jr-text-2)] mt-1">{tier.credits}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <button type="submit" disabled={loading} data-testid="register-submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[var(--jr-brand)] text-white font-medium hover:bg-[var(--jr-brand-2)] transition-colors disabled:opacity-60 glow-brand">
                        {loading ? t("auth.reg_submitting") : t("auth.reg_submit")} <ArrowRight size={16}/>
                    </button>

                    <p className="text-sm text-[var(--jr-text-2)]">
                        {t("auth.reg_have_account")}{" "}
                        <Link to="/login" className="text-[var(--jr-brand)] hover:text-[var(--jr-brand-2)]" data-testid="goto-login">{t("auth.reg_login_link")}</Link>
                    </p>
                </form>
            </div>
        </div>
    );
}

function Field({ label, v, onChange, type = "text", testid, required, min }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</label>
            <input
                type={type}
                required={required}
                minLength={min}
                value={v}
                data-testid={testid}
                onChange={onChange}
                className="mt-2 w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-[var(--jr-text-3)] focus:border-[var(--jr-brand)] outline-none transition-colors"
            />
        </div>
    );
}
